import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { RemindersFilter } from '@aid/reminders/types/classes/reminders-filter';
import { CustomersService } from '@aid/customers/services';
import { InsurancesService } from '@aid/insurances/services';
import { RemindersParams } from '@aid/reminders/types/interfaces';
import { Customer } from '@aid/customers/types/classes';
import { Insurance } from '@aid/insurances/types/classes';
import { map, tap } from 'rxjs/operators';

@Injectable()
export class RemindersFiltersService {
  private filter = new BehaviorSubject<RemindersFilter | null>(null);
  private sidenavOpened = new BehaviorSubject<boolean>(false);

  constructor(
    private customersService: CustomersService,
    private insurancesService: InsurancesService
  ) {}

  applyFilter(filter: RemindersFilter) {
    this.filter.next(filter);
  }

  /**
   * Create Filter from Query Params
   * If the current filter has the same customer or insurance we take the customer and insurance
   * from current filter
   * If not we get the details from the API
   */
  createFilterFromParams(params: RemindersParams): Observable<RemindersFilter> {
    const currentFilter = this.filtersValue;
    const filter = RemindersFilter.createFromParams(params, currentFilter);

    const customerId = filter.customer ? undefined : params.customer;
    const insuranceId = filter.insurance ? undefined : params.insurance;
    if (!customerId && !insuranceId) {
      this.filter.next(filter);
      return of(filter);
    }

    const customer$: Observable<Customer | undefined> = customerId
      ? this.customersService.getBasic(customerId)
      : of(filter.customer);
    if (insuranceId) {
      this.insurancesService.setCustomer(params.customer);
    }
    const insurance$: Observable<Insurance | undefined> = insuranceId
      ? this.insurancesService.getBasic(insuranceId)
      : of(filter.insurance);

    return combineLatest([customer$, insurance$]).pipe(
      tap(([customer, insurance]) => {
        filter.customer = customer;
        filter.insurance = insurance;
        this.applyFilter(filter);
      }),
      map(() => filter)
    );
  }

  get filter$(): Observable<RemindersFilter | null> {
    return this.filter.asObservable();
  }

  get filtersValue(): RemindersFilter | null {
    return this.filter.value;
  }

  get sidenavOpened$(): Observable<boolean> {
    return this.sidenavOpened.asObservable();
  }

  toggleSidenav() {
    this.sidenavOpened.next(!this.sidenavOpened.value);
  }
}
